/**
 * @fileoverview Protected route wrapper component for authenticated routes.
 *
 * Redirects unauthenticated users to the login page, preserving the
 * original destination for redirect after successful login.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts';
import type { UserRole } from '../contexts';

/**
 * Props for the ProtectedRoute component.
 */
interface ProtectedRouteProps {
  /** The component/element to render if authenticated */
  children: JSX.Element;
  /** Optional: Required roles for access. If not specified, any authenticated user can access. */
  requiredRoles?: UserRole[];
}

/**
 * Protected route wrapper component.
 *
 * Wraps route elements that require authentication. Redirects to login
 * if user is not authenticated, preserving the attempted destination
 * for redirect after successful login.
 *
 * Can optionally require specific roles for access.
 *
 * @param props - Component props
 * @param props.children
 * @param props.requiredRoles
 * @returns The protected content, loading spinner, or redirect
 *
 * @example
 * ```tsx
 * // Any authenticated user
 * <Route path="/dashboard" element={
 *   <ProtectedRoute>
 *     <Dashboard />
 *   </ProtectedRoute>
 * } />
 *
 * // Only admin users
 * <Route path="/admin" element={
 *   <ProtectedRoute requiredRoles={['admin']}>
 *     <AdminPanel />
 *   </ProtectedRoute>
 * } />
 * ```
 */
export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps): JSX.Element {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the current location so we can redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements if specified
  if (requiredRoles && requiredRoles.length > 0 && !hasRole(...requiredRoles)) {
    // User is authenticated but lacks required role - show forbidden message
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white">403</h1>
          <p className="mt-2 text-slate-400">You do not have permission to access this page.</p>
          <a
            href="/dashboard"
            className="mt-4 inline-block text-primary-400 hover:text-primary-300"
          >
            Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  // User is authenticated (and has required role if specified) - render the protected content
  return children;
}
